import React, { useState, useEffect } from "react";
import { WelcomeCardConfig, CategoryType, QuickActionTemplate } from "./types";
import { QuickActionGrid } from "./QuickActionGrid";
import styles from "./WelcomeScreen.module.css";
import { CardType, useQuickActionsStore, useChatStore, useUIStore, useAuthStore } from "@store";
import { trackEvent } from "../../services/analyticsService";
import { logDebug } from "../../utils/logging";
import { EditNoteIcon, LightbulbIcon, ArrowForwardIcon } from "../common/Icons";
import { formatTimestamp } from "../../utils/dateUtils";

// Welcome message configuration
const WELCOME_CONFIG: {
  title: string;
  subtitle: string;
  cards: WelcomeCardConfig[];
} = {
  title: "Salutări! Ești gata să-ți crești afacerea?",
  subtitle: "Te pot ajuta să creezi copy care convertește, să analizezi idei și strategii, sau să gândim noua ta idee mare. Alege un șablon predefinit sau pur și simplu scrie-mi cu ce te pot ajuta.",
  cards: [
    {
      id: "creaza_draft",
      title: "Creează un draft",
      description: "Generează reclame, postări social media, emailuri și documente strategice",
      icon: <EditNoteIcon className={styles.actionIcon} />,
      cardType: "creaza_draft" as const,
      action: "show_categories" as const,
    },
    {
      id: "obtine_sfaturi",
      title: "Sfaturi strategice",
      description: "Strategii de creștere, idei de marketing și alte sfaturi strategice.",
      icon: <LightbulbIcon className={styles.actionIcon} />,
      cardType: "sfaturi_strategice" as const,
      action: "show_categories" as const,
    },
    /*
    {
      id: "analiza_competitor",
      title: "Analiză competitor",
      description: "Comparează mesajele și găsește lacunele.",
      icon: <QueryStatsIcon className={styles.actionIcon} />,
      cardType: "analiza_competitor" as const,
      action: "show_categories" as const,
    }
    */
  ],
};

interface WelcomeScreenProps {
  onActionSelect: (
    cardType: string,
    category?: CategoryType,
    template?: QuickActionTemplate,
  ) => void;
}

export const WelcomeScreen: React.FC<WelcomeScreenProps> = ({
  onActionSelect,
}) => {
  const [selectedCard, setSelectedCard] = useState<WelcomeCardConfig | null>(
    null,
  );
  const [showCategories, setShowCategories] = useState(false);

  // Store hooks
  const {
    isLoading: isLoadingTemplates,
    error: templateError,
    loadTemplates,
    clearError,
    isInitialized,
  } = useQuickActionsStore();

  const {
    chatHistorySessions,
    fetchSessionSummaries,
    setSelectedSessionIdForView,
  } = useChatStore();

  const { openChatToFullscreen } = useUIStore();

  // Load templates on component mount
  useEffect(() => {
    if (!isInitialized) {
      logDebug("[WelcomeScreen] Loading templates on startup...");
      loadTemplates().catch((error) => {
        logDebug("[WelcomeScreen] Error loading templates:", error);
      });
      trackEvent("welcome_screen_initialized");
    }
  }, [isInitialized, loadTemplates]);

  // Load history summaries if empty
  const initialDataLoaded = useAuthStore((s) => !!s.jwt);

  // Load history summaries if empty and authenticated
  useEffect(() => {
    if (initialDataLoaded && chatHistorySessions.length === 0) {
      logDebug("[WelcomeScreen] Fetching session summaries...");
      fetchSessionSummaries().catch(err => logDebug("Failed to load history summaries", err));
    } else if (!initialDataLoaded) {
      logDebug("[WelcomeScreen] Waiting for auth to load history summaries...");
    }
  }, [initialDataLoaded, chatHistorySessions.length, fetchSessionSummaries]);

  const handleCardClick = (card: WelcomeCardConfig) => {
    logDebug("[WelcomeScreen] Card clicked:", card.cardType);

    trackEvent("welcome_card_clicked", {
      card_type: card.cardType,
      action: card.action,
    });

    if (card.action === "show_categories") {
      setSelectedCard(card);
      setShowCategories(true);
    } else {
      onActionSelect(card.cardType);
    }
  };

  const handleTemplateSelect = (template: QuickActionTemplate) => {
    logDebug("[WelcomeScreen] Template selected:", template.template_id);
    if (selectedCard) {
      onActionSelect(selectedCard.cardType, undefined, template);
    }
  };

  const handleBackToCards = () => {
    logDebug("[WelcomeScreen] Back to cards clicked");
    setSelectedCard(null);
    setShowCategories(false);
    if (templateError) {
      clearError();
    }
  };

  const handleHistoryClick = (sessionId: string) => {
    setSelectedCard(null); // Ensure we exit category view if active (unlikely but safe)
    setSelectedSessionIdForView(sessionId);
  };

  const handleViewAllHistory = () => {
    // Switch to fullscreen mode where history sidebar is visible
    openChatToFullscreen();
  };

  // Render Loading
  if (isLoadingTemplates && !isInitialized) {
    return (
      <div className={styles.welcomeScreen}>
        <div className={styles.welcomeHeader}>
          <h1 className={styles.welcomeTitle}>{WELCOME_CONFIG.title}</h1>
          <p className={styles.welcomeSubtitle}>Se încarcă opțiunile...</p>
        </div>
        <div className={styles.loadingState}>
          <div className={styles.loadingSpinner}></div>
          <p>Se pregătesc opțiunile pentru tine...</p>
        </div>
      </div>
    );
  }

  // Render Error
  if (templateError && !isInitialized) {
    return (
      <div className={styles.welcomeScreen}>
        <div className={styles.welcomeHeader}>
          <h1 className={styles.welcomeTitle}>{WELCOME_CONFIG.title}</h1>
          <p className={styles.welcomeSubtitle}>A apărut o problemă...</p>
        </div>
        <div className={styles.errorState}>
          <p className={styles.errorMessage}>
            Ne pare rău, nu am putut încărca opțiunile disponibile.
          </p>
          <button
            className={styles.retryButton}
            onClick={() => {
              clearError();
              loadTemplates();
            }}
          >
            Încearcă din nou
          </button>
        </div>
      </div>
    );
  }

  // Render Categories View
  if (showCategories && selectedCard) {
    return (
      <div className={styles.welcomeScreen}>
        <div className={styles.welcomeHeader}>
          <button
            className={styles.backButton}
            onClick={handleBackToCards}
            aria-label="Înapoi la opțiuni principale"
          >
            ← Înapoi
          </button>
          <h2 className={styles.categoryTitle}>
            <span className={styles.categoryIcon}>{selectedCard.icon}</span>
            {selectedCard.title}
          </h2>
          <p className={styles.categorySubtitle}>
            Alege categoria care te interesează:
          </p>
        </div>
        <QuickActionGrid
          onTemplateSelect={handleTemplateSelect}
          cardType={selectedCard.cardType as CardType}
        />
      </div>
    );
  }

  // Render Main Welcome View (List of Cards + Recent)
  const recentSessions = chatHistorySessions.slice(0, 2);

  return (
    <div className={styles.welcomeScreen}>
      <div className={styles.welcomeHeader}>
        <h1 className={styles.welcomeTitle}>{WELCOME_CONFIG.title}</h1>
        <p className={styles.welcomeSubtitle}>{WELCOME_CONFIG.subtitle}</p>
      </div>

      <div className={styles.cardList}>
        <p className={styles.sectionTitle}>ÎNCEPE RAPID</p>
        {WELCOME_CONFIG.cards.map((card) => (
          <button
            key={card.id}
            className={styles.actionCardRow}
            onClick={() => handleCardClick(card)}
            aria-label={`${card.title} - ${card.description}`}
          >
            <div className={styles.cardIconWrapper}>
              {card.icon}
            </div>
            <div className={styles.cardContent}>
              <h3 className={styles.cardTitle}>{card.title}</h3>
              <p className={styles.cardDescription}>{card.description}</p>
            </div>
            <div className={styles.cardArrow}>
              <ArrowForwardIcon />
            </div>
          </button>
        ))}
      </div>

      {recentSessions.length > 0 && (
        <div className={styles.recentSection}>
          <div className={styles.recentHeader}>
            <p className={styles.sectionTitle}>RECENTE</p>
            <button className={styles.viewAllButton} onClick={handleViewAllHistory}>Vezi toate</button>
          </div>
          <div className={styles.recentList}>
            {recentSessions.map(session => (
              <button key={session.session_id} className={styles.recentItem} onClick={() => handleHistoryClick(session.session_id)}>
                <span className={styles.recentIcon}>🕒</span>
                <span className={styles.recentText}>
                  {session.title || formatTimestamp(session.last_activity_at)}
                </span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
